import { Component, OnInit, ViewContainerRef } from '@angular/core';
import { QueryEditorBase } from 'src/core/models/query/query-editor-base';
import { QueryBuilderCategoryDto } from 'src/core/models/query/query-builder-category.dto';
import { QueryGroupQueryItemDto } from 'src/core/models/query/query-group-query-item.dto';
import { IQueryBuilderCategoryItem } from 'src/core/models/query/query-builder-category-item.interface';
import { ConversationFilterableFields } from 'src/core/models/conversation/conversation-filterable-fields.enum';
import { QueryFieldDataType } from 'src/core/models/query/query-field-data-type.enum';
import { QueryConstants } from 'src/core/constants/query-constant';
import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { SettingConstants } from 'src/core/constants/setting-constant';

@Component({
  selector: 'ca-query-tree-node-query-group',
  templateUrl: './query-tree-node-query-group.component.html',
  styleUrls: ['./query-tree-node-query-group.component.scss'],
  standalone: false,
})
export class QueryTreeNodeQueryGroupComponent extends QueryEditorBase implements OnInit {
  categories: QueryBuilderCategoryDto[];
  payload: QueryGroupQueryItemDto;

  isEditing = false;
  editableText: string;

  private categoryItemTitleVisible: string[] = [];

  get categoryName(): string {
    return this.payload?.name || this.localizationService.instant('Query::QueryGroup');
  }

  set categoryName(value: string) {
    if (!this.payload) return;
    this.payload.name = (value ?? '').trim();
  }

  get defaultName(): string {
    return this.localizationService.instant('Query::QueryGroup');
  }

  get viewName(): string {
    return this.payload?.name && this.payload.name.trim().length
      ? this.payload.name
      : this.defaultName;
  }

  enableEdit() {
    this.isEditing = true;

    this.editableText = this.payload?.name ?? '';
    setTimeout(() => {
      const el = document.querySelector<HTMLInputElement>('input[autofocus]');
      el?.select();
    }, 50);
  }

  saveAndClose() {
    const v = (this.editableText ?? '').trim();
    this.payload.name = v;
    this.isEditing = false;
  }

  cancelEdit() {
    this.isEditing = false;
  }

  onMenuOpened(eventArgs) {
    const nodeButtons = (
      this.viewContainerRef.element.nativeElement as HTMLElement
    ).querySelectorAll('.show-on-hover');

    nodeButtons.forEach(btn => {
      btn.classList.add('show-on-hover-disabled');
    });
  }

  onMenuClosed(eventArgs) {
    const nodeButtons = (
      this.viewContainerRef.element.nativeElement as HTMLElement
    ).querySelectorAll('.show-on-hover');

    nodeButtons.forEach((btn: HTMLElement) => {
      btn.classList.remove('show-on-hover-disabled');
      btn.style.display = 'none';
    });
  }

  onAddNodeClick(category: QueryBuilderCategoryDto, categoryItem: IQueryBuilderCategoryItem) {
    if (categoryItem.dataType === this.queryFieldDataType.QueryParser) {
      this.queryBuilder.requestQueryParse(this.node);
    } else {
      this.queryBuilder.addNodeFromAnotherNode(this.node, category, categoryItem);
    }
  }

  isCategoryItemVisible(categoryItem: IQueryBuilderCategoryItem): boolean {
    if (this.queryConstants.categoriesToHide.includes(categoryItem.dataType)) {
      return false;
    }

    if (this.categoryItemTitleVisible.indexOf(categoryItem.title) >= 0) {
      return false;
    }
    if (
      categoryItem.dataType === this.queryFieldDataType.OrderedSimpleTerm ||
      categoryItem.conversationTypes.includes(this.queryField.Conversation_Type)
    ) {
      return false;
    }
    if (categoryItem.conversationTypes.length > 0) {
      const selectedConversationTypeIds = this.queryBuilder.currentConversationTypeIds ?? [];

      return (
        selectedConversationTypeIds.length > 0 &&
        selectedConversationTypeIds.every(id => categoryItem.conversationTypes.includes(id))
      );
    } else {
      return true;
    }
  }

  isCategoryItemDisabled(categoryItem: IQueryBuilderCategoryItem): boolean {
    if (
      categoryItem.dataType == this.queryFieldDataType.MergeQuery &&
      this.queryBuilder.currentQuery &&
      this.queryBuilder.currentQuery.canMergeWithAnotherQuery === false
    ) {
      return true;
    } else {
      return false;
    }
  }

  constructor(
    private viewContainerRef: ViewContainerRef,
    public queryField: ConversationFilterableFields,
    public queryFieldDataType: QueryFieldDataType,
    private queryConstants: QueryConstants,
    private localizationService: LocalizationService,
    private config: ConfigStateService,
  ) {
    super();
    this.editableText = this.localizationService.instant('Query::QueryGroup');

    let emotionEnabled =
      this.config.getSetting(SettingConstants.EmotionEnabled)?.toLowerCase() === 'true';
    let genderEnabled =
      this.config.getSetting(SettingConstants.GenderEnabled)?.toLowerCase() === 'true';
    let sentimentEnabled =
      this.config.getSetting(SettingConstants.SentimentEnabled)?.toLowerCase() === 'true';
    let acousticEnabled =
      this.config.getSetting(SettingConstants.AcousticEnabled)?.toLowerCase() === 'true';

    if (!emotionEnabled) {
      this.categoryItemTitleVisible.push('QueryBuilder:AgentTensionRatio');
      this.categoryItemTitleVisible.push('QueryBuilder:CustomerTensionRatio');
      this.categoryItemTitleVisible.push('QueryBuilder:MonotonicityRatio');
    }
    if (!sentimentEnabled) {
      this.categoryItemTitleVisible.push('QueryBuilder:SentimentTrend');
      this.categoryItemTitleVisible.push('QueryBuilder:CustomerSentiment');
      this.categoryItemTitleVisible.push('QueryBuilder:AgreementStatus');
      this.categoryItemTitleVisible.push('QueryBuilder:AgentSentiment');
      this.categoryItemTitleVisible.push('QueryBuilder:NegativeContent');
    }

    if (!genderEnabled) {
      this.categoryItemTitleVisible.push('QueryBuilder:CustomerGender');
    }

    if (!acousticEnabled) {
      this.categoryItemTitleVisible.push('QueryBuilder:AgentSpeed');
      this.categoryItemTitleVisible.push('QueryBuilder:CallOverlapDuration');
      this.categoryItemTitleVisible.push('QueryBuilder:CallOverlapRatio');
      this.categoryItemTitleVisible.push('QueryBuilder:CallMaxOverlapDuration');
      this.categoryItemTitleVisible.push('QueryBuilder:CallMaxSilenceDuration');
      this.categoryItemTitleVisible.push('QueryBuilder:CallSilenceRatio');
      this.categoryItemTitleVisible.push('QueryBuilder:AgentBlockRatio');
      this.categoryItemTitleVisible.push('QueryBuilder:CustomerBlockRatio');
      this.categoryItemTitleVisible.push('CallSilenceDurationBeginning');
      this.categoryItemTitleVisible.push('CallSilenceDurationEnd');
      this.categoryItemTitleVisible.push('AgentInterruptionCountPerMinute');
      this.categoryItemTitleVisible.push('QueryBuilder:CustomerShare');
      this.categoryItemTitleVisible.push('AgentHesitationCountPerMinute');
    }
  }

  ngOnInit() {
    this.categories = this.queryBuilder.visibleCategories;
    this.payload = this.node.categoryItem.payload as QueryGroupQueryItemDto;
    this.node.validationStatus = true;
  }
}
